/**
 * Every analytics event the web app is allowed to emit, and the exact event name GA4 receives for each one.
 */
export enum GoogleAnalyticsEvents {
    /** SPA route change. Fired by `<PageViewTracker />`. */
    PageView = 'page_view',

    /** User-initiated sign-in succeeded  */
    Login = 'login',

    /** User-initiated sign-out succeeded. */
    Logout = 'logout',

    /** User-initiated search. */
    Search = 'search',

    /** STIS - User-initiated document preview. */
    DocumentPreview = 'document_preview',

    /** STIS - User-initiated document open. */
    DocumentOpen = 'document_open',

    /** STIS - User-initiated document filter apply. */
    DocumentFilterApply = 'document_filter_apply',

    /** STIS - User-initiated document download. */
    DocumentDownload = 'document_download',

    /** VIN Session - Session started. */
    VinSessionStart = 'vin_session_start',

    /** VIN Session - Session ended. */
    VinSessionEnd = 'vin_session_end',

    /** VIN Session - Session abandoned. */
    VinSessionAbandon = 'vin_session_abandon',

    /** CID/CVN — User-initiated start of a CID/CVN check. */
    StartCidCvnCheck = 'start_cid_cvn_check',

    /** CID/CVN - User-initiated selection of a CID/CVN image. */
    CidCvnImageSelect = 'cid_cvn_image_select',

    /** CID/CVN - User-initiated run of CID/CVN check. */
    CidCvnCheckResult = 'cid_cvn_check_result',

    /** CID/CVN - User-initiated scan of CID/CVN image. */
    CidCvnImageScan = 'cid_cvn_image_scan',

    /** CVT - User uploaded CSV file for CVT diagnostic. */
    UploadCvtDiagnostic = 'upload_cvt_diagnostic',

    /** Techline case has been opened */
    TechlineCaseOpen = 'techline_case_open',

    /** Techline case has been submitted */
    TechlineSubmitCase = 'techline_submit_case',

    /** Techline case has been saved as a draft */
    TechlineSaveDraftCase = 'techline_save_draft_case',

    /** Techline case has been closed */
    TechlineCloseCase = 'techline_close_case',

    /** Techline case draft has been created */
    TechlineCaseDraftCreate = 'techline_case_draft_create',

    /** Worksheet draft has been deleted */
    WorksheetDeleteDraft = 'worksheet_delete_draft',

    /** Worksheet draft has been saved */
    WorksheetSaveDraft = 'worksheet_save_draft',

    /** Worksheet has been submitted */
    WorksheetSubmit = 'worksheet_submit',

    /** Worksheet has been opened */
    WorksheetOpen = 'worksheet_open',

    /** QMR - User-initiated view of a QMR document. */
    QmrView = 'qmr_view',

    /** NQR - User-initiated view of an NQR document. */
    NqrView = 'nqr_view',

    /** QMR/NQR - User-initiated creation of a draft. */
    QmrNqrDraftCreate = 'qmr_nqr_draft_create',

    /** QMR/NQR - User-initiated submission of a draft. */
    QmrNqrSubmit = 'qmr_nqr_submit',

    /** QMR/NQR - Report delete failed. */
    QmrNqrDelete = 'qmr_nqr_delete',

    /** QMR/NQR - Report validation failed. */
    QmrNqrValidationFail = 'qmr_nqr_validation_fail',
}

/**
 * A loosely typed bag of GA4 event parameters, used where the concrete event is not known statically (context
 * sources, transport plumbing, and other code that merges or forwards properties without inspecting them).
 *
 * Call sites that DO know their event should not use this type; they get per-event property checking through
 * {@link GoogleAnalyticsEventPropertyMap} and {@link GoogleAnalyticsTrackArgs} instead.
 */
export type GoogleAnalyticsEventProperties = Record<string, unknown>;

/**
 * The event-name type used everywhere an event is referenced. It is the enum itself rather than a union of its
 * string values, because `GoogleAnalyticsEventPropertyMap` is keyed by enum members: a template-literal union of the
 * values cannot index an enum-keyed map. Making this the enum also means raw strings are rejected at `track()`, so
 * every call site must name its event through the enum.
 */
export type GoogleAnalyticsEventName = GoogleAnalyticsEvents;

/** Property shape per event. Use `undefined` for events with no properties. */
export type GoogleAnalyticsEventPropertyMap = {
    [GoogleAnalyticsEvents.PageView]: {
        page_location: string;
        page_referrer: string;
        page_title: string;
        module_name: string;
    };

    [GoogleAnalyticsEvents.Login]: undefined;

    [GoogleAnalyticsEvents.Logout]: undefined;

    [GoogleAnalyticsEvents.Search]: {
        success: boolean;
        search_term?: string;
        results_count?: number;
        error_type?: string;
        search_type?: string;
    };

    [GoogleAnalyticsEvents.DocumentFilterApply]: {
        entry_point: string;
        filter_count: number;
        filters_applied: string;
        search_type?: string;
    };

    [GoogleAnalyticsEvents.DocumentOpen]: {
        document_id?: string;
        document_type?: string;
        error_type?: string;
        success: boolean;
        entry_point?: string;
    };

    [GoogleAnalyticsEvents.DocumentPreview]: {
        document_id: string;
        document_type?: string;
        success?: boolean;
        error_type?: string;
        entry_point?: string;
    };

    [GoogleAnalyticsEvents.DocumentDownload]: {
        document_id: string;
        document_type: string;
        error_type?: string;
        success: boolean;
        entry_point?: string;
    };

    [GoogleAnalyticsEvents.VinSessionStart]: {
        model: string;
        model_year: number;
        model_number: string;
    };

    [GoogleAnalyticsEvents.VinSessionEnd]: {
        model: string;
        model_year: number;
        model_number: string;
        duration_ms?: number;
        end_reason: string;
    };

    [GoogleAnalyticsEvents.VinSessionAbandon]: {
        model: string;
        model_year: number;
        model_number: string;
        duration_ms?: number;
    };

    [GoogleAnalyticsEvents.StartCidCvnCheck]: undefined;

    [GoogleAnalyticsEvents.CidCvnImageSelect]: {
        success: boolean;
        error_type?: string;
        error_code?: string;
    };

    [GoogleAnalyticsEvents.CidCvnCheckResult]: {
        success: boolean;
        result?: string;
        error_type?: string;
        error_code?: string;
    };

    [GoogleAnalyticsEvents.CidCvnImageScan]: {
        success: boolean;
        error_type?: string;
        error_code?: string;
    };

    [GoogleAnalyticsEvents.UploadCvtDiagnostic]: {
        success: boolean;
        error_type?: string;
    };

    [GoogleAnalyticsEvents.TechlineCaseOpen]: {
        case_status: string;
        case_type?: string;
    };

    [GoogleAnalyticsEvents.TechlineSubmitCase]: {
        success: boolean;
        case_status?: string;
        case_type?: string;
    };

    [GoogleAnalyticsEvents.TechlineSaveDraftCase]: {
        success: boolean;
        case_status?: string;
        case_type?: string;
    };

    [GoogleAnalyticsEvents.TechlineCloseCase]: {
        success: boolean;
        case_status?: string;
        case_type?: string;
    };

    [GoogleAnalyticsEvents.TechlineCaseDraftCreate]: {
        success: boolean;
    };

    [GoogleAnalyticsEvents.WorksheetSaveDraft]: {
        success: boolean;
    };

    [GoogleAnalyticsEvents.WorksheetSubmit]: {
        success: boolean;
    };
    [GoogleAnalyticsEvents.WorksheetDeleteDraft]: {
        success: boolean;
    };
    [GoogleAnalyticsEvents.WorksheetOpen]: {
        open_mode: string;
        worksheet_group?: string;
    };

    [GoogleAnalyticsEvents.NqrView]: {
        report_status: string;
    };

    [GoogleAnalyticsEvents.QmrView]: {
        report_status: string;
    };

    [GoogleAnalyticsEvents.QmrNqrDraftCreate]: {
        success: boolean;
        report_type: string;
    };

    [GoogleAnalyticsEvents.QmrNqrSubmit]: {
        success: boolean;
        report_type?: string;
        attachment_count?: number;
        dtc_count?: number;
        fail_code?: string;
    };

    [GoogleAnalyticsEvents.QmrNqrDelete]: {
        success: boolean;
        report_type?: string;
    };
    [GoogleAnalyticsEvents.QmrNqrValidationFail]: undefined;
};

/**
 * The trailing argument list `track()` accepts for a given event, expressed as a tuple so the properties argument can
 * be required or omitted per event rather than always optional.
 *
 * It exists to make the two failure modes impossible: passing properties to an event that takes none, and forgetting
 * properties for an event that requires them. When the event's entry in {@link GoogleAnalyticsEventPropertyMap} is
 * `undefined` the argument is optional (and must be `undefined` if given); otherwise it is required and typed to that
 * entry exactly.
 *
 * @template TEventName - the specific {@link GoogleAnalyticsEvents} member being tracked, which selects the property
 * shape from the map
 *
 * @example
 * // Login maps to `undefined`, so the properties argument may be omitted entirely.
 * track(GoogleAnalyticsEvents.Login);
 *
 * // WorksheetSubmit maps to `{ success: boolean }`, so the properties argument is required.
 * track(GoogleAnalyticsEvents.WorksheetSubmit, { success: true });
 */
export type GoogleAnalyticsTrackArgs<TEventName extends GoogleAnalyticsEventName> =
    GoogleAnalyticsEventPropertyMap[TEventName] extends undefined
        ? [properties?: undefined]
        : [properties: GoogleAnalyticsEventPropertyMap[TEventName]];

/**
 * Compile-time proof that {@link GoogleAnalyticsEvents} and {@link GoogleAnalyticsEventPropertyMap} cover exactly the
 * same set of events: the first element fails to resolve if an enum member has no map entry, the second if the map has
 * an entry that is not an enum member.
 *
 * The map is hand-maintained, and TypeScript does not require a mapped-type-style object type to be complete. Without
 * this guard, adding an enum member and forgetting its map entry would compile silently, and the omission would only
 * surface later as an event tracked with unchecked properties.
 */
type _AssertExhaustive = [
    GoogleAnalyticsEventName extends keyof GoogleAnalyticsEventPropertyMap ? true : never,
    keyof GoogleAnalyticsEventPropertyMap extends GoogleAnalyticsEventName ? true : never,
];

/**
 * Forces {@link _AssertExhaustive} to actually be evaluated. A type alias alone is inert; assigning `[true, true]` to
 * it is what turns a missing entry on either side into a compile error, because an unmatched element resolves to
 * `never` and `true` is not assignable to `never`.
 *
 * The value is unused at runtime (see the `void` below, which satisfies no-unused-vars without changing behavior).
 */
const _googleAnalyticsEventsExhaustive: _AssertExhaustive = [true, true];
void _googleAnalyticsEventsExhaustive;
